// Hand-written, not generated: native radio buttons drawn as one segmented
// control. Lifted from the planner (components/planner/inspector) so the
// Training check-in shares it; shadcn's RadioGroup is a Radix widget, and
// these stay real <input type=radio> for the keyboard, browser agents and
// the tests' `getByRole("radio")`.
import { useId } from "react";
import PropTypes from "prop-types";
import { cn } from "@/lib/utils";

/**
 * A labelled group of real radio buttons drawn as segments (tools, views,
 * Keep). Native inputs: arrow keys move the choice, the group has one tab
 * stop, and screen readers and browser agents see "radio, checked". The
 * checked segment is filled (moss) so the mode in use is never in doubt.
 *
 * `options`: `[{value, label, Icon?, shortcut?, detail?, disabled?}]` (a
 * disabled option stays in view, its detail saying why). `stacked` lays
 * them out one per row with the detail under the label (Keep). `compact`
 * puts a small label under each icon below `sm` (a 375 px phone fits the
 * five tools on one row, each still named on screen — icons alone left a
 * phone guessing), and beside it from `sm`. `phoneGrid` lays them out two
 * to a row below `sm`, each its own bordered, rounded cell, and as the
 * joined row from `sm`: for long labels that would wrap the joined row
 * (Training's four-option Workload), where a wrapped row broke its
 * borders and corners.
 */
function RadioSegments({
  legend,
  hideLegend = false,
  options,
  value,
  onChange,
  disabled = false,
  stacked = false,
  compact = false,
  phoneGrid = false,
  className,
}) {
  const name = useId();
  let row = "inline-flex flex-wrap rounded-md border border-input shadow-xs";
  let cell =
    "min-h-11 min-w-11 justify-center px-3 first:rounded-l-md last:rounded-r-md md:min-h-9 [&:not(:first-child)]:border-l [&:not(:first-child)]:border-input";
  if (stacked) {
    row = "flex flex-col gap-1.5";
    cell = "min-h-11 rounded-md border border-input px-3 py-2 md:min-h-9";
  } else if (phoneGrid) {
    row =
      "grid grid-cols-2 gap-1.5 sm:inline-flex sm:flex-wrap sm:gap-0 sm:rounded-md sm:border sm:border-input sm:shadow-xs";
    cell =
      "min-h-11 min-w-11 justify-center rounded-md border border-input px-3 text-center md:min-h-9 sm:rounded-none sm:border-0 sm:first:rounded-l-md sm:last:rounded-r-md sm:[&:not(:first-child)]:border-l sm:[&:not(:first-child)]:border-input";
  }
  return (
    <fieldset className={cn("min-w-0", className)} disabled={disabled}>
      <legend
        className={cn(
          hideLegend ? "sr-only" : "mb-1.5 text-sm font-medium leading-none",
        )}
      >
        {legend}
      </legend>
      <div className={cn(row)}>
        {options.map((option) => {
          const checked = option.value === value;
          const Icon = option.Icon;
          const underIcon = compact && Boolean(Icon);
          return (
            <label
              key={option.value}
              title={
                option.shortcut
                  ? `${option.label} (${option.shortcut})`
                  : undefined
              }
              className={cn(
                "relative flex cursor-pointer items-center gap-2 text-sm font-medium transition-colors select-none",
                "has-[:focus-visible]:z-10 has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring",
                "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
                cell,
                underIcon && "max-sm:flex-col max-sm:gap-0.5 max-sm:px-1",
                checked
                  ? "border-primary bg-primary text-primary-foreground"
                  : "bg-transparent text-foreground hover:bg-muted",
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name={name}
                value={option.value}
                checked={checked}
                disabled={option.disabled || undefined}
                aria-keyshortcuts={option.shortcut}
                onChange={() => onChange(option.value)}
              />
              {Icon && <Icon aria-hidden="true" className="size-4 shrink-0" />}
              <span className={cn(stacked && "flex flex-col gap-0.5")}>
                <span className={cn(underIcon && "max-sm:text-[0.625rem] max-sm:leading-none")}>
                  {option.label}
                </span>
                {option.detail && (
                  <>
                    {/* A real space: the name reads "Keep west (wM8)", not "west(wM8)". */}
                    {" "}
                    <span
                      className={cn(
                        "text-xs font-normal",
                        checked ? "text-primary-foreground/85" : "text-muted-foreground",
                      )}
                    >
                      {option.detail}
                    </span>
                  </>
                )}
              </span>
              {option.shortcut && (
                <kbd
                  aria-hidden="true"
                  className={cn(
                    "hidden rounded border px-1 font-mono text-[0.65rem] leading-4 md:inline",
                    checked
                      ? "border-primary-foreground/40 text-primary-foreground/85"
                      : "border-border text-muted-foreground",
                  )}
                >
                  {option.shortcut}
                </kbd>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

RadioSegments.propTypes = {
  legend: PropTypes.node.isRequired,
  hideLegend: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      Icon: PropTypes.elementType,
      shortcut: PropTypes.string,
      detail: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  stacked: PropTypes.bool,
  compact: PropTypes.bool,
  phoneGrid: PropTypes.bool,
  className: PropTypes.string,
};

export { RadioSegments };
